import {
  IOptimizedImage,
  OptimizedImageUrlFields,
} from '@/infrastructure/interfaces/i-optimized-image';

const FILEMANAGER_URL = process.env.FILEMANAGER_URL || '';
const API_DOMAIN = process.env.API_DOMAIN || '';
const FILEMANAGER_PROXY_PATH =
  process.env.NEXT_PUBLIC_FILEMANAGER_PROXY_PATH || '/content-images';
const IS_STATIC_OR_PROD =
  process.env.NEXT_PUBLIC_STATIC_EXPORT === 'true' ||
  process.env.NODE_ENV === 'production';
const NORMALIZED_FILEMANAGER_URL = FILEMANAGER_URL.replace(/\/$/, '');
const BACKEND_IMAGE_PATH_PREFIXES = ['/feature-covers/', '/images/'];

const isAbsoluteUrl = (url: string) => /^[a-z][a-z\d+\-.]*:\/\//i.test(url);

const joinUrl = (baseUrl: string, path: string) => {
  if (!baseUrl) return path;
  return `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
};

const isBackendImagePath = (url: string) =>
  BACKEND_IMAGE_PATH_PREFIXES.some((prefix) => url.startsWith(prefix));

interface IImageUrlOptions {
  backendImageDomain?: string;
}

const shouldProxyFilemanagerUrl = (sourceUrl: string) =>
  IS_STATIC_OR_PROD &&
  sourceUrl !== '' &&
  sourceUrl === NORMALIZED_FILEMANAGER_URL;

const normalizeImageUrl = (
  url: string,
  filemanagerUrl: string,
  proxyPath: string,
  options: IImageUrlOptions = {}
) => {
  const sourceUrl = filemanagerUrl.replace(/\/$/, '');
  const backendImageDomain = options.backendImageDomain || API_DOMAIN;
  const shouldUseFilemanagerProxy = shouldProxyFilemanagerUrl(sourceUrl);

  if (isAbsoluteUrl(url) || url.startsWith('//')) {
    if (shouldUseFilemanagerProxy && url.startsWith(sourceUrl)) {
      return url.replace(sourceUrl, proxyPath);
    }

    return url;
  }

  if (isBackendImagePath(url)) {
    return joinUrl(backendImageDomain, url);
  }

  if (url.startsWith(`${proxyPath}/`)) {
    return url;
  }

  if (shouldUseFilemanagerProxy) {
    if (url.startsWith(sourceUrl)) {
      return url.replace(sourceUrl, proxyPath);
    }

    if (url.startsWith('/')) {
      return `${proxyPath}${url}`;
    }
  }

  if (sourceUrl && !url.includes(filemanagerUrl)) {
    return joinUrl(filemanagerUrl, url);
  }

  return url;
};

/**
 * Mutates a given object by adding domain for image urls
 * @param image
 */
export const addDomainForImage = (
  image: IOptimizedImage,
  filemanagerUrl = FILEMANAGER_URL,
  options: IImageUrlOptions = {}
) => {
  if (!image) return;

  const normalizedProxyPath = FILEMANAGER_PROXY_PATH.replace(/\/$/, '');

  OptimizedImageUrlFields.map((field) => {
    const originalValue = image[field];
    if (!originalValue) {
      return;
    }

    image[field] = normalizeImageUrl(
      originalValue,
      filemanagerUrl,
      normalizedProxyPath,
      options
    );
  });
};
